import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { CheckOutlined, RemoveRedEyeOutlined, VisibilityOffOutlined } from "@mui/icons-material";
import { formatLocaleDateString, getCognitoUserAttribute } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useIsMobile } from "hooks/useIsMobile";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { INotification, INotificationAction } from "store/notificationStore";
import { markAllNotifAsSeen } from "store/notificationStore";
import { toggleNavDrawer } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";

const Notification = ({ data, close = () => {} }: { data: INotification; close?: () => void }) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { optionsPaginationNotifications } = useAppSelector((state) => state.notification);
    const { isMobile } = useIsMobile();
    const { user } = useAuthenticator((context) => [context.user]);
    const userAttributes = getCognitoUserAttribute("custom:accountType", user);
    const baseURL =
        userAttributes === "beneficiary"
            ? import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT
            : ["publisher", "admin"].includes(userAttributes)
              ? import.meta.env.REACT_APP_API_ENDPOINT
              : "";

    const { companyInfos, handleSwitchCompany } = useCompanySettings({
        portalType: userAttributes === "beneficiary" ? "beneficiary" : "book",
    });

    const handleMarkAsRead = (id: number) => {
        if (optionsPaginationNotifications.seen === "all") {
            !!baseURL && dispatch(markAllNotifAsSeen({ baseUrl: baseURL, id: [id] }));
        } else {
            const notifClicked = document.querySelector(`li[data-animation-id="${id}"]`);
            if (notifClicked) {
                notifClicked.classList.add("removing");
                setTimeout(() => {
                    notifClicked.classList.replace("removing", "removed");
                    !!baseURL && dispatch(markAllNotifAsSeen({ baseUrl: baseURL, id: [id] }));
                }, 500);
            }
        }
    };

    const handleActionClick = (action: INotificationAction, id: number, companyId: number) => {
        // TODO : Remove to hook or context ?
        handleMarkAsRead(id);
        if (user) {
            if (userAttributes === "beneficiary") {
                switch (action.type) {
                    case "get":
                        switch (action.data) {
                            case "profile":
                            case "beneficiary":
                                navigate(`/settings/account/${action.payload.companyId}`);
                                break;
                            case "term":
                                if (action.payload.titleId) {
                                    navigate(`/titles/${action.payload.titleId}`, {
                                        state: { tab: "2", termId: action.payload.termId },
                                    });
                                } else {
                                    navigate(`/statements`, { state: { termId: action.payload.termId } });
                                }
                                break;
                            case "statement":
                                navigate(`/statements`, {
                                    state: {
                                        termId: action.payload.termId,
                                        periodId: action.payload.periodId,
                                        titleId: action.payload.titleId,
                                    },
                                });
                                break;
                            case "document":
                                navigate(`/settings/documents/${action.payload.companyId}`);
                                break;
                            case "title":
                                navigate(`/titles/${action.payload.titleId}`);
                                break;
                            case "publication":
                                navigate(`/publications/${action.payload.titleId}`, {
                                    state: {
                                        tab: "2",
                                        from: "notifications",
                                        beneficiaryId: action.payload.beneficiaryId,
                                        articleId: action.payload.articleId,
                                    },
                                });
                                break;
                            case "external":
                                window.open(action.payload.url, "_blank");
                                break;
                            default:
                                break;
                        }
                        break;
                    // TODO : Switch case for decline and approve
                    default:
                        break;
                }
                close();
            } else if (["publisher", "admin"].includes(userAttributes)) {
                const isOtherCompany = companyInfos?.id != companyId;
                let navigateProps: { url: string; state?: any } = {
                    url: "",
                    state: undefined,
                };
                switch (action.type) {
                    case "get":
                        switch (action.data) {
                            case "profile":
                            case "beneficiary":
                                // navigate(`/beneficiars/${action.payload.beneficiaryId}`);
                                navigateProps = {
                                    url: `/beneficiars/${action.payload.beneficiaryId}`,
                                };
                                break;
                            case "term":
                                navigateProps = {
                                    url: action.payload.workId
                                        ? `/works/${action.payload.workId}`
                                        : `/titles/${action.payload.titleId}`,
                                    state: { state: { termId: action.payload.termId, tab: "2" } },
                                };
                                break;
                            case "document":
                                // navigate(`/beneficiars/${action.payload.beneficiaryId}`, {state: {tab: "4"}});
                                navigateProps = {
                                    url: `/beneficiars/${action.payload.beneficiaryId}`,
                                    state: { state: { tab: "5" } },
                                };
                                break;
                            case "title":
                                // navigate(`/titles/${action.payload.titleId}`);
                                navigateProps = { url: `/titles/${action.payload.titleId}` };
                                break;
                            case "publication":
                                // navigate(`/publications/${action.payload.titleId}`, {state: {tab: "2", from: "notifications", beneficiaryId: action.payload.beneficiaryId, articleId: action.payload.articleId}});
                                navigateProps = {
                                    url: `/publications/${action.payload.titleId}`,
                                    state: {
                                        state: {
                                            tab: "2",
                                            from: "notifications",
                                            beneficiaryId: action.payload.beneficiaryId,
                                            articleId: action.payload.articleId,
                                        },
                                    },
                                };
                                break;
                            case "agreement":
                                navigateProps = {
                                    url: `/agreements/${action.payload.agreementId}`,
                                };
                                break;
                            case "import":
                                navigateProps = {
                                    url: "/settings/history",
                                };
                                break;
                            case "external":
                                window.open(action.payload.url, "_blank");
                                break;
                            case "transferTermSales":
                                navigateProps = {
                                    url: `/transfer-terms`,
                                    state: {
                                        state: {
                                            tabValue: "0",
                                            // There's a typo in the payload but that comes from backend.
                                            // If I fix it in backend, old notifications won't work -> do we still do something?
                                            transferTermSalesId: action.payload.tranferTermSalesId,
                                        },
                                    },
                                };
                                break;
                            default:
                                break;
                        }
                        if (!isOtherCompany) {
                            navigate(navigateProps.url, navigateProps.state ? navigateProps.state : undefined);
                        } else {
                            handleSwitchCompany(companyId);
                            window.location.href = `${window.location.origin}${navigateProps.url}`;
                        }
                        break;
                    // TODO : Switch case for decline and approve
                    default:
                        break;
                }
                close();
            }
        }
        if (isMobile) dispatch(toggleNavDrawer(false));
    };

    return (
        <li className={`notification${data.seen ? " seen" : ""}`} data-animation-id={data.id}>
            <div className={`notification-infos`}>
                <div className="from-wrapper">
                    <p className="from">
                        {t("Payment.From")}
                        {data.avatarUrl ? <img src={data.avatarUrl} alt="avatar" /> : " "}
                        {data.name}
                    </p>
                    <span className="date">
                        {formatLocaleDateString({ date: data.created, options: { numeric: true, timeZone: "UTC" } })}
                    </span>
                </div>
                <p className="details">{data.description}</p>
            </div>
            {!!data.actions.length && (
                <div className="notification-actions">
                    <button
                        style={{ border: "1px solid #EAEAEA" }}
                        onClick={() => handleActionClick(data.actions[0], data.id, data.companyId)}
                    >
                        {data.actions[0].type === "get" ? (
                            <RemoveRedEyeOutlined />
                        ) : data.actions[0].type === "approve" || data.actions[0].type === "decline" ? (
                            <CheckOutlined />
                        ) : (
                            ""
                        )}
                        {t(data.actions[0].title)}
                    </button>
                    {
                        !data.seen && (
                            <button style={{ border: "1px solid #141516" }} onClick={() => handleMarkAsRead(data.id)}>
                                <VisibilityOffOutlined sx={{ pointerEvents: "none", width: "16px" }} />
                                {t("Notifications.MarkAsRead")}
                            </button>
                        )
                        // :
                        // <button className='seen-btn' style={{border: "1px solid #141516"}}>
                        //     <CheckOutlined sx={{pointerEvents: "none", width: "16px"}}/>
                        //     {t('Notifications.Read')}
                        // </button>
                    }
                </div>
            )}
        </li>
    );
};

export default Notification;
